// MobilePlayer — player da superfície MOBILE (touch). Parede A4.
//
// REUSA o MESMO core de mídia da TV: `@app-iptv/player` (createPlayer →
// WebPlayer, hls.js/mpegts). NÃO recria a engine — só a camada de
// CONTROLES/overlay ganha variante touch. NADA de Norigin / usePlayerKeys /
// controle remoto aqui (a TV continua intocada).
//
// Desenho (docs/specs/mobile-app-ux.md §5):
//   RETRATO  → vídeo 16:9 no topo (não fullscreen) + metadados abaixo
//              (título/sinopse; detalhe rico é A5). Overlay aparece com TAP,
//              auto-hide ~3,5s. Botão ⤢ entra em fullscreen paisagem.
//   PAISAGEM → vídeo tela inteira (safe-area/notch), overlay touch:
//              play/pause central, scrubber arrastável (VOD), -10s/+10s por
//              DOUBLE-TAP nas metades esquerda/direita, ← fecha (volta retrato).
//
// <video> singleton (memória project_browser_autoplay_pattern): a TV usa um
// <video> persistente em portal. A superfície mobile é mutuamente exclusiva da
// TV (resolveSurface), então este player tem o SEU <video> próprio — sem
// destruir/recriar o da TV. Respeitamos o gesto de autoplay: o <video> é criado
// 1× no mount e o player só faz attach/load/destroy via o contrato do core
// (consumers JAMAIS tocam em video.src). play() só dispara após o gesto de TAP
// que abriu a tela (mobile exige gesto pra áudio) — mas tentamos no 'ready' e,
// se o browser bloquear, o tap central destrava.
//
// Toda a matemática de seek/scrubber vive em mobile-player-seek.ts (pura,
// testada sem DOM). Aqui só ligamos eventos de toque ao core + ao <video>.

import { getCapability } from '@app-iptv/device'
import { useTranslation } from '@app-iptv/i18n'
import { createPlayer } from '@app-iptv/player'
import type { MediaPlayer } from '@app-iptv/player'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

import { api } from '../api'
import { LoadingPhrase } from '../components/LoadingPhrase'
import { Spinner } from '../components/Spinner'
import { StreamLimitOverlay } from '../components/TrialOverlay'
import { useAccountExpired } from '../hooks/useAccountExpired'
import { useProviderQueryKey } from '../hooks/useScopedQueryKey'
import { useTrialGuard } from '../hooks/useTrialGuard'
import { isPersistableDuration } from '../lib/playback-progress'
import { savePosition } from '../local-db'
import { notifyPlaybackStarted } from '../net-priority'
import { track } from '../qoe'
import { rewriteStreamUrl } from '../stream-url'
import { setTrialBlocked } from '../trial-block-store'

import {
  barFraction,
  fractionFromTime,
  hasSeekControls,
  isDoubleTap,
  nextStatusOnBuffering,
  seekAfterDoubleTap,
  seekSideFromX,
  timeFromFraction,
  type MobilePlayerStatus,
  type SeekSide,
} from './mobile-player-seek'
import styles from './MobilePlayer.module.css'

/** Tipos que o player mobile sabe abrir nesta parede. Live/série reusam o
 *  mesmo caminho (signStream cobre os 4 tipos); A4 foca VOD. */
export type MobilePlayerKind = 'vod' | 'series' | 'channel'

export interface MobilePlayerProps {
  /** Id do conteúdo a ASSINAR (vodId / episodeId / channelId). Pra série, é o
   *  episodeId — signStream('series', episodeId), igual à TV. */
  id: string
  /** Tipo — decide o signStream e se há scrubber (VOD/série têm; live não). */
  kind: MobilePlayerKind
  /** Título já conhecido pelo card (evita "piscar" antes do getVodInfo). */
  title?: string | undefined
  /** Poster já conhecido pelo card (salvar posição com capa bonita). */
  poster?: string | null | undefined
  /** Retomar a partir de N segundos (continuar assistindo). */
  resumeFrom?: number | undefined
  /** Contexto de EPISÓDIO de série (vem da tela de detalhe, A5). Quando
   *  presente, salvamos a posição como series-episode (destrava o "continuar"
   *  de séries, pendente da A4). seriesId/episodeId obrigatórios pra salvar;
   *  season/episode entram no registro pra exibir "Continuar T·E". */
  seriesId?: string | undefined
  episodeId?: string | undefined
  seasonNumber?: number | undefined
  episodeNumber?: number | undefined
  /** Fechar o player (← volta pra Home/detalhe). */
  onClose: () => void
}

// Auto-hide do overlay. Subido de 3,5s pra 4,8s: a persona mais velha lê os
// controles e quando vai agir o overlay já tinha sumido (atrito reportado no
// gate da A4). 4,8s dá tempo sem poluir a tela. Pausado NÃO esconde (ver
// armAutoHide), que é o comportamento esperado de player. Exportado pra teste.
export const HIDE_AFTER_MS = 4800

/** Mapeia o tipo do card pro tipo do signStream. */
function signKind(kind: MobilePlayerKind): 'vod' | 'series' | 'live' {
  if (kind === 'channel') return 'live'
  return kind
}

function formatClock(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '00:00'
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = Math.floor(sec % 60)
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

/** Trava orientação landscape (best-effort). API só existe em alguns browsers
 *  e exige fullscreen ativo — falha silenciosa onde não der (iOS Safari). */
function lockLandscape(): void {
  try {
    const orientation = (
      screen as unknown as { orientation?: { lock?: (o: string) => Promise<void> } }
    ).orientation
    void orientation?.lock?.('landscape').catch(() => undefined)
  } catch {
    // sem API de orientação — usuário gira o aparelho manualmente.
  }
}

function unlockOrientation(): void {
  try {
    const orientation = (
      screen as unknown as { orientation?: { unlock?: () => void } }
    ).orientation
    orientation?.unlock?.()
  } catch {
    // no-op
  }
}

export function MobilePlayer({
  id,
  kind,
  title,
  poster,
  resumeFrom = 0,
  seriesId,
  episodeId,
  seasonNumber,
  episodeNumber,
  onClose,
}: MobilePlayerProps): JSX.Element {
  const { t } = useTranslation()
  const accountExpired = useAccountExpired()

  // Refs do core/DOM. videoRef é o NOSSO <video> mobile (não o singleton da TV).
  const videoRef = useRef<HTMLVideoElement>(null)
  const playerRef = useRef<MediaPlayer | null>(null)
  const shellRef = useRef<HTMLDivElement>(null)
  const barRef = useRef<HTMLDivElement>(null)
  const ttffReported = useRef(false)
  const loadStart = useRef(0)
  // Metadados pra salvar posição (ref pra não recriar o effect de load).
  const metaRef = useRef<{ title: string; poster: string | null }>({
    title: title ?? '',
    poster: poster ?? null,
  })
  // Contexto de episódio (série) pra salvar a posição como series-episode na
  // limpeza do effect de load. Ref pra não inchar as deps do effect (os campos
  // são estáveis por episodeId, que JÁ é o `id` e re-cria o effect ao trocar).
  const epRef = useRef<{
    seriesId?: string | undefined
    episodeId?: string | undefined
    seasonNumber?: number | undefined
    episodeNumber?: number | undefined
  }>({ seriesId, episodeId, seasonNumber, episodeNumber })
  useEffect(() => {
    epRef.current = { seriesId, episodeId, seasonNumber, episodeNumber }
  }, [seriesId, episodeId, seasonNumber, episodeNumber])

  // 'buffering' = re-buffer no MEIO da reprodução (engine perdeu dados). Distinto
  // de 'loading' (1ª carga): aqui já houve play, então mostramos um indicador
  // discreto SEM voltar pro spinner cheio nem fingir que está 'playing'. 'ended'
  // = VOD chegou ao fim → estado de fim com replay/voltar (a TV ainda não trata
  // isso; melhoria só do mobile, sem tocar a TV).
  const [status, setStatus] = useState<MobilePlayerStatus>('loading')
  // O `loading` atual é o do "Reassistir" (estado de fim → início), não a carga
  // inicial: a frase de sofá é só da carga inicial de filme/série.
  const [replayLoading, setReplayLoading] = useState(false)
  const [errorReason, setErrorReason] = useState<'blocked' | 'expired' | 'generic'>('generic')
  const [fullscreen, setFullscreen] = useState(false)
  const [overlayVisible, setOverlayVisible] = useState(true)
  const [position, setPosition] = useState(resumeFrom)
  const [duration, setDuration] = useState(0)
  // Arraste do scrubber: enquanto true, position mostra o ponto sob o dedo
  // (preview) e só commitamos no video no touchend.
  const [scrubbing, setScrubbing] = useState(false)
  const [scrubFraction, setScrubFraction] = useState(0)
  // Indicador efêmero de double-tap seek (◀◀10 / ▶▶10).
  const [seekFlash, setSeekFlash] = useState<SeekSide | null>(null)
  const seekFlashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Estado do double-tap (último tap pra detectar o segundo).
  const lastTapRef = useRef<{ ts: number; side: SeekSide } | null>(null)

  const showScrubber = hasSeekControls(kind)

  // ===== Sinopse/título (best-effort) — detalhe rico é A5 ===== //
  const infoQ = useQuery({
    queryKey: useProviderQueryKey(['mobile-player-info', kind, id]),
    queryFn: () => api.getVodInfo(id),
    enabled: kind === 'vod',
    staleTime: 10 * 60 * 1000,
  })
  const displayTitle = infoQ.data?.name ?? title ?? ''
  const synopsis = infoQ.data?.plot ?? null

  useEffect(() => {
    metaRef.current = {
      title: infoQ.data?.name ?? title ?? '',
      poster: infoQ.data?.poster ?? poster ?? null,
    }
  }, [infoQ.data, title, poster])

  // ===== Auto-hide do overlay ===== //
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const armAutoHide = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
    const v = videoRef.current
    // Só esconde quando está tocando — pausado/carregando deixa visível.
    if (v && !v.paused) {
      hideTimerRef.current = setTimeout(() => setOverlayVisible(false), HIDE_AFTER_MS)
    }
  }, [])

  const revealOverlay = useCallback(() => {
    setOverlayVisible(true)
    armAutoHide()
  }, [armAutoHide])

  // ===== Lifecycle do core de mídia (cria o WebPlayer 1x por conteúdo) ===== //
  useEffect(() => {
    const video = videoRef.current
    if (!id || !video) return

    if (accountExpired) {
      setErrorReason('expired')
      setStatus('error')
      return
    }

    let cancelled = false
    loadStart.current = performance.now()
    ttffReported.current = false
    setReplayLoading(false)
    setStatus('loading')

    const player = createPlayer(getCapability('cpuTier'))
    playerRef.current = player
    player.attach(video)

    player.on('ready', () => {
      if (resumeFrom > 0) player.seek(resumeFrom)
      // play() pós-attach: o gesto que abriu a tela costuma valer; se o browser
      // bloquear (sem gesto), o tap central destrava. Não chamamos video.load().
      void player.play()
    })
    player.on('playing', () => {
      // 1º FRAME = fim do caminho crítico de rede: libera a telemetria
      // represada desde o sign (net-priority).
      notifyPlaybackStarted()
      setStatus('playing')
      armAutoHide()
      if (!ttffReported.current) {
        ttffReported.current = true
        const ttff = Math.round(performance.now() - loadStart.current)
        track({
          type: 'ttff',
          ms: ttff,
          channelId: `${signKind(kind)}:${id}`,
          title: metaRef.current.title || undefined,
          ts: Math.floor(Date.now() / 1000),
        })
      }
    })
    player.on('paused', () => setStatus('paused'))
    // Re-buffer no meio: indicador discreto + status NÃO fica "playing" falso.
    // O próximo 'playing' (acima) limpa. Não toca no overlay/auto-hide.
    player.on('buffering', () => {
      setStatus((s) => nextStatusOnBuffering(s))
    })
    // Fim do VOD: mostra estado de fim (replay/voltar). O overlay normal de
    // controles dá lugar ao card de fim; revelamos pra ficar sempre acessível.
    player.on('ended', () => {
      setStatus('ended')
      setOverlayVisible(true)
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
    })

    void api
      .signStream(signKind(kind), id)
      .then((sign) => {
        if (cancelled) return
        const fmt: 'hls' | 'mp4' = sign.format === 'hls' ? 'hls' : 'mp4'
        const finalUrl = rewriteStreamUrl(sign.url, fmt, sign)
        const source: { url: string; type: 'hls' | 'mp4'; startPosition?: number } = {
          url: finalUrl,
          type: fmt,
        }
        if (resumeFrom > 0) source.startPosition = resumeFrom
        void player.load(source)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        console.error('[mobile-player] sign/load failed', err)
        setStatus('error')
      })

    return () => {
      cancelled = true
      // Salva posição (continuar assistindo). VOD salva direto pelo id; SÉRIE
      // salva como series-episode quando o detalhe (A5) passou seriesId/
      // episodeId — isso destrava o "continuar" de séries, pendente da A4.
      const now = Math.floor(Date.now() / 1000)
      const ep = epRef.current
      // `isPersistableDuration`, não `> 0`: `Infinity > 0` é true (ver lib/playback-progress).
      const durationOk = isPersistableDuration(video.duration)
      if (kind === 'vod' && durationOk && id) {
        void savePosition({
          id: `vod:${id}`,
          type: 'vod',
          itemId: id,
          positionSec: video.currentTime,
          durationSec: video.duration,
          lastWatchedAt: now,
          title: metaRef.current.title,
          poster: metaRef.current.poster,
        })
      } else if (kind === 'series' && durationOk && ep.seriesId && ep.episodeId) {
        void savePosition({
          id: `series-episode:${ep.seriesId}:${ep.episodeId}`,
          type: 'series-episode',
          itemId: ep.episodeId,
          seriesId: ep.seriesId,
          episodeId: ep.episodeId,
          ...(ep.seasonNumber !== undefined ? { seasonNumber: ep.seasonNumber } : {}),
          ...(ep.episodeNumber !== undefined ? { episodeNumber: ep.episodeNumber } : {}),
          positionSec: video.currentTime,
          durationSec: video.duration,
          lastWatchedAt: now,
          title: metaRef.current.title,
          poster: metaRef.current.poster,
        })
      }
      void player.destroy()
      playerRef.current = null
    }
  }, [id, kind, resumeFrom, accountExpired, armAutoHide])

  // ===== Espelha timeupdate do <video> → position/duration ===== //
  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    const onTime = (): void => {
      if (!scrubbing) setPosition(v.currentTime)
      if (isPersistableDuration(v.duration)) setDuration(v.duration)
    }
    v.addEventListener('timeupdate', onTime)
    v.addEventListener('loadedmetadata', onTime)
    return () => {
      v.removeEventListener('timeupdate', onTime)
      v.removeEventListener('loadedmetadata', onTime)
    }
  }, [scrubbing])

  // ===== Fullscreen + trava de orientação ===== //
  // Entrar: requestFullscreen no shell (não no <video>, senão esconde overlay)
  // + lock landscape best-effort. Sair: exitFullscreen + unlock. Sincroniza com
  // mudanças externas (ex: gesto do sistema) via fullscreenchange.
  useEffect(() => {
    const onChange = (): void => {
      const isFs = typeof document !== 'undefined' && document.fullscreenElement !== null
      setFullscreen(isFs)
      if (isFs) lockLandscape()
      else unlockOrientation()
    }
    document.addEventListener('fullscreenchange', onChange)
    return () => {
      document.removeEventListener('fullscreenchange', onChange)
    }
  }, [])

  const enterFullscreen = useCallback(() => {
    const el = shellRef.current
    if (!el) return
    if (typeof el.requestFullscreen === 'function') {
      void el.requestFullscreen().then(lockLandscape).catch(() => undefined)
    } else {
      // Sem Fullscreen API (iOS Safari não dá fullscreen em div): simula via
      // classe CSS de "fake fullscreen" (cobre a viewport) + sugere girar.
      setFullscreen(true)
      lockLandscape()
    }
    revealOverlay()
  }, [revealOverlay])

  const exitFullscreen = useCallback(() => {
    if (typeof document !== 'undefined' && document.fullscreenElement) {
      void document.exitFullscreen().catch(() => undefined)
    } else {
      setFullscreen(false)
    }
    unlockOrientation()
    revealOverlay()
  }, [revealOverlay])

  // Limpa o timer de auto-hide e o de flash ao desmontar; garante sair de
  // fullscreen/orientação se o player desmontar enquanto expandido.
  useEffect(() => {
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
      if (seekFlashTimerRef.current) clearTimeout(seekFlashTimerRef.current)
      if (typeof document !== 'undefined' && document.fullscreenElement) {
        void document.exitFullscreen().catch(() => undefined)
      }
      unlockOrientation()
    }
  }, [])

  // ===== Trial guard (mesma regra do VOD da TV) ===== //
  const trial = useTrialGuard(Boolean(id))
  const trialBlocked = trial.blocked || trial.startBlock !== null
  const trialExhausted = trial.blocked || trial.startBlock === 'trial_blocked'
  useEffect(() => {
    if (!trialBlocked) return
    const v = videoRef.current
    playerRef.current?.pause?.()
    v?.pause()
    const onPlay = (): void => v?.pause()
    v?.addEventListener('play', onPlay)
    return () => v?.removeEventListener('play', onPlay)
  }, [trialBlocked])
  useEffect(() => {
    if (trialExhausted) setTrialBlocked(true)
  }, [trialExhausted])

  // ===== Ações de playback ===== //
  const togglePlayPause = useCallback(() => {
    const v = videoRef.current
    if (!v) return
    if (v.paused) void v.play()
    else v.pause()
    revealOverlay()
  }, [revealOverlay])

  const applySeek = useCallback((sec: number) => {
    const v = videoRef.current
    if (!v) return
    v.currentTime = sec
    setPosition(sec)
  }, [])

  // Replay do estado de fim: volta ao início e toca de novo. O evento 'playing'
  // do core devolve o status pra 'playing' e rearma o auto-hide.
  const replay = useCallback(() => {
    const v = videoRef.current
    if (!v) return
    v.currentTime = 0
    setPosition(0)
    setReplayLoading(true)
    setStatus('loading')
    void v.play()
    revealOverlay()
  }, [revealOverlay])

  const flashSeek = useCallback((side: SeekSide) => {
    setSeekFlash(side)
    if (seekFlashTimerRef.current) clearTimeout(seekFlashTimerRef.current)
    seekFlashTimerRef.current = setTimeout(() => setSeekFlash(null), 600)
  }, [])

  // Botões VISÍVEIS de ±10s (mesma ação do double-tap, com affordance). A
  // persona leiga nunca descobre o double-tap; o botão dá o caminho visível.
  const skipBy = useCallback(
    (side: SeekSide) => {
      const v = videoRef.current
      const cur = v?.currentTime ?? position
      applySeek(seekAfterDoubleTap(cur, duration, side))
      flashSeek(side)
      revealOverlay()
    },
    [position, duration, applySeek, flashSeek, revealOverlay],
  )

  // Tap na área do vídeo: detecta double-tap (seek) vs single (toggle overlay).
  // Em VOD/série, double-tap nas metades faz -/+10s; em live (sem scrubber) o
  // tap só alterna o overlay.
  const onVideoAreaTap = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect()
      const localX = e.clientX - rect.left
      const side = seekSideFromX(localX, rect.width)
      const now = Date.now()
      const next = { ts: now, side }
      const doubled = showScrubber && isDoubleTap(lastTapRef.current, next)
      if (doubled) {
        lastTapRef.current = null
        const v = videoRef.current
        const cur = v?.currentTime ?? position
        const target = seekAfterDoubleTap(cur, duration, side)
        applySeek(target)
        flashSeek(side)
        revealOverlay()
        return
      }
      lastTapRef.current = next
      // Single tap: alterna o overlay (mostra/esconde).
      if (overlayVisible) {
        setOverlayVisible(false)
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
      } else {
        revealOverlay()
      }
    },
    [showScrubber, position, duration, applySeek, flashSeek, revealOverlay, overlayVisible],
  )

  // ===== Scrubber arrastável (VOD/série) ===== //
  const fractionFromPointer = useCallback((clientX: number): number => {
    const bar = barRef.current
    if (!bar) return 0
    const rect = bar.getBoundingClientRect()
    return barFraction(clientX - rect.left, rect.width)
  }, [])

  const onScrubStart = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (duration <= 0) return
      e.currentTarget.setPointerCapture?.(e.pointerId)
      setScrubbing(true)
      const f = fractionFromPointer(e.clientX)
      setScrubFraction(f)
      setPosition(timeFromFraction(f, duration))
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
      setOverlayVisible(true)
    },
    [duration, fractionFromPointer],
  )

  const onScrubMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!scrubbing || duration <= 0) return
      const f = fractionFromPointer(e.clientX)
      setScrubFraction(f)
      setPosition(timeFromFraction(f, duration))
    },
    [scrubbing, duration, fractionFromPointer],
  )

  const onScrubEnd = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!scrubbing) return
      e.currentTarget.releasePointerCapture?.(e.pointerId)
      setScrubbing(false)
      applySeek(timeFromFraction(scrubFraction, duration))
      armAutoHide()
    },
    [scrubbing, scrubFraction, duration, applySeek, armAutoHide],
  )

  // ===== Derivados ===== //
  const fillFraction = scrubbing ? scrubFraction : fractionFromTime(position, duration)
  const remaining = duration > 0 ? Math.max(0, duration - position) : 0

  const shellClass = [
    styles.shell,
    fullscreen ? styles.fullscreen : styles.portrait,
    status === 'playing' && !overlayVisible ? styles.cursorHidden : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={shellClass} ref={shellRef} data-fullscreen={fullscreen ? 'true' : 'false'}>
      {/* Limite de telas (fechável). Trial esgotado → gate global (TrialGate). */}
      {trial.startBlock === 'stream_limit' ? <StreamLimitOverlay onBack={onClose} /> : null}

      {/* ===== Caixa do vídeo (16:9 no retrato, tela cheia no fullscreen) ===== */}
      <div className={styles.videoBox}>
        {/* playsInline (React) já emite playsinline + webkit-playsinline — sem
            isso o iOS força o <video> a fullscreen nativo e esconde o overlay. */}
        <video ref={videoRef} className={styles.video} controls={false} playsInline />

        {/* Camada de gestos (tap/double-tap) sobre o vídeo. Não cobre os
            controles inferiores (zIndex menor). */}
        <div
          className={styles.gestureLayer}
          onPointerUp={onVideoAreaTap}
          data-testid="gesture-layer"
        />

        {status === 'loading' && (
          <div className={styles.statusOverlay} data-state="loading">
            <Spinner size="lg" label={t('player.loading')} />
            {kind !== 'channel' && !replayLoading ? (
              // Carga inicial de filme/série: a frase de sofá (folha com relógio
              // próprio; desmonta no `playing`/erro, outro conteúdo muda a `key`).
              <LoadingPhrase key={id} surface="touch" className={styles.loadingPhrase} />
            ) : (
              <div className={styles.statusLabel}>{t('player.loading')}</div>
            )}
          </div>
        )}

        {status === 'loading' && (
          // ‹ e título DURANTE a carga (ux-tv, 10/10/2026). A carga agora dura a espera
          // inteira (o 'buffering' do play() não a encerra mais) e o bloco de controles
          // só existe fora de loading/error/ended: sem isto a única saída pela UI
          // sumia. Mesmo botão (alvo de 44px) do topo dos controles, acima do overlay
          // (z 4 > 3). Vale também pro "Reassistir" (rótulo estático).
          <div className={styles.loadingTop} data-testid="loading-top">
            <div className={styles.topBar}>
              <button
                type="button"
                className={styles.iconBtn}
                aria-label={t('common.back')}
                onClick={onClose}
                data-testid="loading-back"
              >
                ‹
              </button>
              <span className={styles.topTitle}>{displayTitle}</span>
              <span className={styles.topSpacer} aria-hidden="true" />
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className={styles.statusOverlay} data-state="error">
            <div className={styles.statusIcon}>⚠️</div>
            <div className={styles.statusLabel}>
              {errorReason === 'expired'
                ? t('player.errorAccountExpired')
                : errorReason === 'blocked'
                  ? t('player.errorContentBlocked')
                  : t('player.errorContent')}
            </div>
            <button type="button" className={styles.errorBtn} onClick={onClose}>
              ‹ {t('common.back')}
            </button>
          </div>
        )}

        {/* Re-buffer no meio: indicador discreto (canto), sem escurecer a tela
            como o loading inicial. Status real é 'buffering' (não 'playing'). */}
        {status === 'buffering' && (
          <div className={styles.bufferingBadge} data-state="buffering">
            <Spinner size="sm" label={t('mobilePlayer.buffering')} />
            <span className={styles.bufferingLabel} aria-hidden="true">
              {t('mobilePlayer.buffering')}
            </span>
          </div>
        )}

        {/* Fim do VOD: card de fim com replay + voltar. A TV não trata 'ended';
            melhoria só do mobile. */}
        {status === 'ended' && (
          <div className={styles.statusOverlay} data-state="ended">
            <div className={styles.statusLabel}>{t('mobilePlayer.ended')}</div>
            <div className={styles.endedActions}>
              <button type="button" className={styles.errorBtn} onClick={replay}>
                ↻ {t('mobilePlayer.replay')}
              </button>
              <button type="button" className={styles.errorBtn} onClick={onClose}>
                ‹ {t('common.back')}
              </button>
            </div>
          </div>
        )}

        {/* Flash de double-tap seek (◀◀ / ▶▶ 10s). */}
        {seekFlash && (
          <div
            className={`${styles.seekFlash} ${seekFlash === 'left' ? styles.seekFlashLeft : styles.seekFlashRight}`}
            data-side={seekFlash}
          >
            {seekFlash === 'left' ? '◀◀' : '▶▶'} 10s
          </div>
        )}

        {/* ===== Overlay de controles (TAP pra mostrar, auto-hide) ===== */}
        {overlayVisible && status !== 'loading' && status !== 'error' && status !== 'ended' && (
          <div className={styles.controls} data-testid="player-controls">
            {/* Topo: ← fecha (retrato) ou volta do fullscreen (paisagem). */}
            <div className={styles.topBar}>
              <button
                type="button"
                className={styles.iconBtn}
                aria-label={t('common.back')}
                onClick={fullscreen ? exitFullscreen : onClose}
              >
                ‹
              </button>
              <span className={styles.topTitle}>{displayTitle}</span>
              <span className={styles.topSpacer} aria-hidden="true" />
            </div>

            {/* Centro: -10s, play/pause grande, +10s. Os botões de skip dão
                affordance VISÍVEL pro seek (double-tap continua valendo como
                atalho). Só em VOD/série (live não tem seek). */}
            <div className={styles.centerRow}>
              {showScrubber && (
                <button
                  type="button"
                  className={styles.skipBtn}
                  data-skip="back"
                  aria-label={t('mobilePlayer.skipBack')}
                  onClick={() => skipBy('left')}
                >
                  <span className={styles.skipGlyph} aria-hidden="true">
                    ↺
                  </span>
                  <span className={styles.skipNum} aria-hidden="true">
                    10
                  </span>
                </button>
              )}
              <button
                type="button"
                className={styles.playBtn}
                aria-label={
                  status === 'playing' || status === 'buffering'
                    ? t('playerExtra.pause')
                    : t('playerExtra.play')
                }
                onClick={togglePlayPause}
              >
                {status === 'playing' || status === 'buffering' ? '⏸' : '▶'}
              </button>
              {showScrubber && (
                <button
                  type="button"
                  className={styles.skipBtn}
                  data-skip="forward"
                  aria-label={t('mobilePlayer.skipForward')}
                  onClick={() => skipBy('right')}
                >
                  <span className={styles.skipGlyph} aria-hidden="true">
                    ↻
                  </span>
                  <span className={styles.skipNum} aria-hidden="true">
                    10
                  </span>
                </button>
              )}
            </div>

            {/* Rodapé: scrubber (VOD/série) + tempo + expandir/fechar. */}
            <div className={styles.bottomBar}>
              {showScrubber && (
                <div className={styles.progressRow}>
                  <span className={styles.timeLabel}>{formatClock(position)}</span>
                  <div
                    ref={barRef}
                    className={styles.scrubber}
                    role="slider"
                    aria-label={t('mobilePlayer.seekBar')}
                    aria-valuemin={0}
                    aria-valuemax={Math.round(duration)}
                    aria-valuenow={Math.round(position)}
                    onPointerDown={onScrubStart}
                    onPointerMove={onScrubMove}
                    onPointerUp={onScrubEnd}
                    onPointerCancel={onScrubEnd}
                  >
                    <div className={styles.scrubTrack}>
                      <div
                        className={styles.scrubFill}
                        style={{ width: `${fillFraction * 100}%` }}
                      />
                      <div
                        className={styles.scrubThumb}
                        style={{ left: `${fillFraction * 100}%` }}
                      />
                    </div>
                  </div>
                  <span className={styles.timeLabel}>-{formatClock(remaining)}</span>
                </div>
              )}

              <div className={styles.bottomActions}>
                <button
                  type="button"
                  className={styles.iconBtn}
                  aria-label={
                    fullscreen ? t('mobilePlayer.collapse') : t('mobilePlayer.expand')
                  }
                  onClick={fullscreen ? exitFullscreen : enterFullscreen}
                >
                  {fullscreen ? '⤡' : '⤢'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ===== Metadados abaixo (só no retrato) — detalhe rico é A5 ===== */}
      {!fullscreen && (
        <div className={styles.meta} data-testid="player-meta">
          <h1 className={styles.metaTitle}>{displayTitle}</h1>
          {synopsis && <p className={styles.metaSynopsis}>{synopsis}</p>}
        </div>
      )}
    </div>
  )
}
